import React, { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ImageGalleryCarousel from "../ImageGalleryCarousel";
import { isLoggedIn } from "../../../helpers/auth";
import ButtonWithAllottedText from "../../atoms/ButtonWithAllottedText";
import MarkdownText from "../../atoms/MarkdownText";
import SelectDropdown from "../../atoms/SelectDropdown";
import urlPatterns from "../../../urls";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import { useWindowWidthSize } from "../../../helpers/hooks";
import { BREAKPOINT } from "../../../styles/variables";

import { getCashAmountLabel } from "../../../helpers/i18n";
import {
  ButtonsWrapper,
  ContentWrapper,
  Title,
  FullPageContainer,
  ColumnContainer,
  RightColumn,
  LeftColumn,
  ImageGalleryHolder,
  CharacteristicDropdownsWrapper,
} from "./style";

interface MerchCharacteristicValue {
  id: number;
  name: string;
  isSelected: boolean;
}

interface MerchCharacteristic {
  id: number;
  name: string;
  values: MerchCharacteristicValue[];
}

export interface RewardMerchDetailsInfoProps {
  handleAddButtonClick: (redeemWithPoints: boolean) => void;
  productUrlPhotos: string[];
  productName: string | undefined;
  pointCost: number | null | undefined;
  sellingPrice: number | null | undefined;
  canBePurchasedWithCash: boolean;
  isRedeemableWithPoints: boolean;
  imageGalleryWidth: string;
  descriptionMarkdown: string | null | undefined;
  variant: "small" | "large" | "fullPage";
  isTopOffSetEqualToHeader?: boolean;
  isAddButtonGrayed: boolean;
  isAddButtonDisabled: boolean;
  imageCarouselBackgroundColor: string;
  isLocked: boolean;
  characteristics: MerchCharacteristic[];
  onCharacteristicValueSelect: (characteristicId: number, valueId: number) => void;
}

const RewardMerchDetailsInfo = ({
  handleAddButtonClick,
  productUrlPhotos,
  productName,
  pointCost,
  sellingPrice,
  canBePurchasedWithCash,
  isRedeemableWithPoints,
  imageGalleryWidth,
  descriptionMarkdown,
  variant,
  isAddButtonGrayed,
  isAddButtonDisabled,
  isTopOffSetEqualToHeader = true,
  imageCarouselBackgroundColor,
  isLocked,
  characteristics,
  onCharacteristicValueSelect,
}: RewardMerchDetailsInfoProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON, I18N_NAMESPACES.REWARDS, I18N_NAMESPACES.WINE_PAGES]);

  const windowWidth = useWindowWidthSize();
  const isWindowWidthXsDown = windowWidth < BREAKPOINT.XS;

  const renderImageGallery = (): ReactElement => {
    const sliderWidth = isWindowWidthXsDown ? `${windowWidth}px` : imageGalleryWidth;

    return (
      <ImageGalleryCarousel
        sliderWidth={sliderWidth}
        galleryPhotos={productUrlPhotos}
        variant={variant}
        bgColor={imageCarouselBackgroundColor}
        isLocked={isLocked}
      />
    );
  };

  const renderTitle = (): ReactElement => (
    <ContentWrapper topPadding="24px">
      <Title>{productName}</Title>
    </ContentWrapper>
  );

  const renderCharacteristics = (): ReactElement => (
    <CharacteristicDropdownsWrapper>
      {characteristics.map((characteristic) => (
        <SelectDropdown
          key={characteristic.id}
          dropdownItems={characteristic.values}
          setValue={characteristic.values.find((value) => value.isSelected)?.id}
          onChange={(valueId: number) => onCharacteristicValueSelect(characteristic.id!, valueId)}
          addTopMargin
          displayFlex
          hasCheckIcon={false}
          title={characteristic.name}
          label={t("rewards:pleaseSelect")}
          addFilterStyles
        />
      ))}
    </CharacteristicDropdownsWrapper>
  );

  const renderButtons = (): ReactElement => (
    <ContentWrapper topPadding="0px">
      {isLoggedIn() ? (
        <ButtonsWrapper>
          {isRedeemableWithPoints && (
            <ButtonWithAllottedText
              isGrayed={isAddButtonGrayed}
              firstText={t("rewards:addItem")}
              secondText={t("common:ptsAmount", { points: pointCost })}
              onButtonClick={() => handleAddButtonClick(true)}
              isDisabled={isAddButtonDisabled}
            />
          )}
          {canBePurchasedWithCash && (
            <ButtonWithAllottedText
              isGrayed={isAddButtonGrayed}
              firstText={t("rewards:addItem")}
              secondText={getCashAmountLabel(sellingPrice)}
              onButtonClick={() => handleAddButtonClick(false)}
              isDisabled={isAddButtonDisabled}
            />
          )}
        </ButtonsWrapper>
      ) : (
        <Link to={urlPatterns.LOGIN}>
          <ButtonWithAllottedText firstText={t("rewards:signInToAddToBox")} />
        </Link>
      )}
    </ContentWrapper>
  );

  const renderDescriptionMarkdown = (): ReactElement => (
    <>
      {descriptionMarkdown && (
        <ContentWrapper topPadding="4px">
          <MarkdownText markdownText={descriptionMarkdown} />
        </ContentWrapper>
      )}
    </>
  );

  const renderFullPageInfo = (): ReactElement => (
    <FullPageContainer>
      <ColumnContainer>
        <LeftColumn isTopOffSetEqualToHeader={isTopOffSetEqualToHeader}>
          <ImageGalleryHolder>{renderImageGallery()}</ImageGalleryHolder>
        </LeftColumn>
        <RightColumn>
          {renderTitle()}
          {characteristics.length > 0 && renderCharacteristics()}
          {renderButtons()}
          {renderDescriptionMarkdown()}
        </RightColumn>
      </ColumnContainer>
    </FullPageContainer>
  );

  if (variant === "fullPage") {
    return renderFullPageInfo();
  }

  return (
    <>
      {renderImageGallery()}
      {renderTitle()}
      {characteristics.length > 0 && renderCharacteristics()}
      {renderButtons()}
      {renderDescriptionMarkdown()}
    </>
  );
};

export default RewardMerchDetailsInfo;
